import React from 'react';

import {
  AccountBalance as AccountBalanceIcon,
  ManageAccounts as ManageAccountsIcon,
  MonetizationOn as MonetizationIcon,
  PeopleAlt as PeopleAltIcon,
  Settings as SettingsIcon,
  Work as WorkIcon,
} from '@mui/icons-material';
import { Box } from '@mui/material';

import { appRoutes, RouterEntitiesId } from '@clearverify/constants';

import { DEFAULT_AVATAR } from 'src/assets';
import { ItemDetailsLayout } from 'src/components/common';
import { DemoDashboardEntityToggle } from 'src/components/demoDashboardMode';
import { PeoControlledByInfo } from 'src/components/peo';
import { ModalId } from 'src/constants/modals';
import { usePeoById, usePeoParamId } from 'src/hooks/peo';
import { useModalV2 } from 'src/hooks/shared';
import { PeoControlledByConfigurationModal } from 'src/modals/peo';
import { MenuItem } from 'src/types/menu';
import { SxProp } from 'src/types/mui';
import { peoReceivesOwnRevenueShare } from 'src/utils/revenueShare';
import { buildUrl } from 'src/utils/router';

export const PeosDetailLayoutCVStaff: React.FC<React.PropsWithChildren> = ({ children }) => {
  const peoId = usePeoParamId();
  const { openModal } = useModalV2();

  const { peo, updatePeoLogo, isPartnership, loading } = usePeoById(peoId);

  const baseRoute = React.useMemo(() => {
    return isPartnership ? appRoutes.partnerships.detail.peo : appRoutes.peos.detail;
  }, [isPartnership]);

  const builtRoutes = React.useMemo(
    () => ({
      companyInformation: buildUrl(baseRoute.companyInformation, {
        [RouterEntitiesId.peo]: peoId,
      }),
      billing: buildUrl(baseRoute.billing, {
        [RouterEntitiesId.peo]: peoId,
      }),
      staff: buildUrl(baseRoute.staff, {
        [RouterEntitiesId.peo]: peoId,
      }),
      employers: buildUrl(baseRoute.employers, {
        [RouterEntitiesId.peo]: peoId,
      }),
      employees: buildUrl(baseRoute.employees, {
        [RouterEntitiesId.peo]: peoId,
      }),
      settings: buildUrl(baseRoute.settings, {
        [RouterEntitiesId.peo]: peoId,
      }),
    }),
    [baseRoute, peoId],
  );

  const menuItems: Array<MenuItem> = React.useMemo(() => {
    return [
      {
        link: builtRoutes.companyInformation,
        icon: AccountBalanceIcon,
        title: 'Company Information',
      },
      {
        link: builtRoutes.staff,
        icon: ManageAccountsIcon,
        title: 'Staff',
      },
      {
        link: builtRoutes.employers,
        icon: WorkIcon,
        title: 'Employers',
      },
      {
        link: builtRoutes.employees,
        icon: PeopleAltIcon,
        title: 'Employees',
        hide: isPartnership,
      },
      {
        link: builtRoutes.billing,
        icon: MonetizationIcon,
        title: 'Billing',
        hide: !peo || !peoReceivesOwnRevenueShare(peo),
      },
      {
        link: builtRoutes.settings,
        icon: SettingsIcon,
        title: 'Settings',
      },
    ];
  }, [builtRoutes, isPartnership, peo]);

  const handleOpenPeoControlledByConfigurationModal = React.useCallback(() => {
    if (!peoId) return;
    openModal(PeoControlledByConfigurationModal, {
      id: ModalId.PeoControlledByConfiguration,
      peoId,
    });
  }, [openModal, peoId]);

  return (
    <ItemDetailsLayout
      name={peo?.displayName}
      subtitleComponent={<PeoControlledByInfo peoId={peoId} isPartnership={isPartnership} />}
      logoUrl={peo?.logo?.downloadUrl || DEFAULT_AVATAR}
      menuItems={menuItems}
      logoProps={{
        onChange: updatePeoLogo,
        loading,
      }}
      loading={loading}
      onSettingsClick={handleOpenPeoControlledByConfigurationModal}
      sidebarFooter={<DemoDashboardEntityToggle entityType="peos" entityId={peoId} />}
    >
      <Box sx={childrenContainerStyles}>{children}</Box>
    </ItemDetailsLayout>
  );
};

const childrenContainerStyles: SxProp = theme => ({
  [theme.breakpoints.up('sm')]: {
    minHeight: '80vh',
  },
});
